import { useState, useEffect, useCallback, useMemo } from 'react';
import { getTeacherStudentsTutorSummary } from '../api/teacherTutorAnalyticsApi';
import type {
  TeacherStudentsSummaryOut,
  TeacherStudentSummaryItem,
  TeacherStudentsSummaryParams,
} from '../types/teacherTutorAnalytics';

type SortField = 'student_id' | 'minutes' | 'sessions' | 'score' | 'last_active';
type SortDirection = 'asc' | 'desc';

const DEFAULT_DAYS = 30;

interface UseTeacherTutorAnalyticsOptions {
  initialDays?: number;
}

interface UseTeacherTutorAnalyticsReturn {
  data: TeacherStudentsSummaryOut | null;
  loading: boolean;
  error: string | null;
  days: number;
  setDays: (days: number) => void;
  refetch: () => void;
  // Backend filters
  startDate: string;
  setStartDate: (v: string) => void;
  endDate: string;
  setEndDate: (v: string) => void;
  studentId: string;
  setStudentId: (v: string) => void;
  classId: string;
  setClassId: (v: string) => void;
  grade: string;
  setGrade: (v: string) => void;
  skip: number;
  setSkip: (v: number) => void;
  limit: number;
  setLimit: (v: number) => void;
  dateRangeError: string | null;
  totalFromResponse: number | null;
  resetFilters: () => void;
  // Client-side filtering and sorting
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  sortField: SortField;
  setSortField: (field: SortField) => void;
  sortDirection: SortDirection;
  setSortDirection: (direction: SortDirection) => void;
  filteredAndSortedData: TeacherStudentSummaryItem[];
}

export function useTeacherTutorAnalytics(
  opts: UseTeacherTutorAnalyticsOptions = {}
): UseTeacherTutorAnalyticsReturn {
  const { initialDays = DEFAULT_DAYS } = opts;
  const [data, setData] = useState<TeacherStudentsSummaryOut | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState(initialDays);

  // Backend filter state
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [studentId, setStudentId] = useState('');
  const [classId, setClassId] = useState('');
  const [grade, setGrade] = useState('');
  const [skip, setSkipState] = useState(0);
  const [limit, setLimit] = useState(0); // 0 = no pagination (backward compatible)
  const [dateRangeError, setDateRangeError] = useState<string | null>(null);
  const [totalFromResponse, setTotalFromResponse] = useState<number | null>(null);

  // Client-side filtering and sorting
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('student_id');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const setSkip = useCallback((v: number) => setSkipState(v), []);

  const setStudentIdAndResetSkip = useCallback((v: string) => {
    setStudentId(v);
    setSkipState(0);
  }, []);
  const setClassIdAndResetSkip = useCallback((v: string) => {
    setClassId(v);
    setSkipState(0);
  }, []);
  const setGradeAndResetSkip = useCallback((v: string) => {
    setGrade(v);
    setSkipState(0);
  }, []);
  const setStartDateAndResetSkip = useCallback((v: string) => {
    setStartDate(v);
    setSkipState(0);
  }, []);
  const setEndDateAndResetSkip = useCallback((v: string) => {
    setEndDate(v);
    setSkipState(0);
  }, []);

  const buildParams = useCallback((): TeacherStudentsSummaryParams | null => {
    const hasStart = startDate.trim() !== '';
    const hasEnd = endDate.trim() !== '';
    if (hasStart !== hasEnd) {
      setDateRangeError('Please select both start date and end date');
      return null;
    }
    setDateRangeError(null);

    const params: TeacherStudentsSummaryParams = {};
    if (hasStart && hasEnd) {
      params.start_date = startDate.trim();
      params.end_date = endDate.trim();
    } else {
      params.days = days;
    }
    if (studentId.trim()) params.student_id = studentId.trim();
    if (classId.trim()) params.class_id = classId.trim();
    if (grade.trim()) params.grade = grade.trim();
    if (limit > 0) {
      params.limit = limit;
      params.skip = skip;
    }
    return params;
  }, [days, startDate, endDate, studentId, classId, grade, skip, limit]);

  const fetchData = useCallback(async () => {
    const params = buildParams();
    if (params == null) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await getTeacherStudentsTutorSummary(params);

      const validatedResult: TeacherStudentsSummaryOut = {
        ok: result.ok ?? true,
        days: result.days ?? days,
        total_students: result.total_students ?? 0,
        summaries: result.summaries ?? [],
        aggregate: {
          total_tutor_minutes: result.aggregate?.total_tutor_minutes ?? 0,
          total_sessions: result.aggregate?.total_sessions ?? 0,
          avg_overall_score: result.aggregate?.avg_overall_score ?? null,
          speaking_level_distribution: result.aggregate?.speaking_level_distribution ?? {},
        },
      };
      if (result.start_date != null) validatedResult.start_date = result.start_date;
      if (result.end_date != null) validatedResult.end_date = result.end_date;
      if (result.skip != null) validatedResult.skip = result.skip;
      if (result.limit != null) validatedResult.limit = result.limit;
      if (result.total != null) validatedResult.total = result.total;

      setData(validatedResult);
      setTotalFromResponse(validatedResult.total ?? null);
    } catch (err: any) {
      let message = 'Failed to load teacher analytics';

      if (err.response?.status === 401) {
        message = 'Please login again';
      } else if (err.response?.status === 403) {
        message = 'Access denied - teacher analytics not available for your role';
      } else if (err.response?.status === 400) {
        message = err.response?.data?.message ?? 'Invalid request parameters';
      } else if (err.response?.status === 404) {
        message = 'Analytics service not found - feature may still be in development';
      } else if (err.response?.status === 500) {
        message = 'Server error - analytics service may be temporarily unavailable';
      } else if (!err.response) {
        message = 'Cannot connect to analytics service';
      }

      setError(message);
      setData(null);
      setTotalFromResponse(null);
    } finally {
      setLoading(false);
    }
  }, [buildParams, days]);

  const refetch = useCallback(() => {
    fetchData();
  }, [fetchData]);

  const handleSetDays = useCallback((newDays: number) => {
    setDays(newDays);
    setStartDate('');
    setEndDate('');
    setSkipState(0);
  }, []);

  const resetFilters = useCallback(() => {
    setDays(DEFAULT_DAYS);
    setStartDate('');
    setEndDate('');
    setStudentId('');
    setClassId('');
    setGrade('');
    setSkipState(0);
    setLimit(0);
    setDateRangeError(null);
  }, []);

  // Refetch when any backend filter or pagination changes
  useEffect(() => {
    fetchData();
  }, [days, startDate, endDate, studentId, classId, grade, skip, limit, fetchData]);

  // Filter and sort data (client-side, for display)
  const filteredAndSortedData = useMemo(() => {
    if (!data?.summaries) return [];

    let filtered = data.summaries;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((item) =>
        item.student_id.toLowerCase().includes(term)
      );
    }

    filtered.sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case 'student_id':
          aValue = a.student_id;
          bValue = b.student_id;
          break;
        case 'minutes':
          aValue = a.summary.total_tutor_minutes;
          bValue = b.summary.total_tutor_minutes;
          break;
        case 'sessions':
          aValue = a.summary.sessions_count;
          bValue = b.summary.sessions_count;
          break;
        case 'score':
          aValue = a.summary.latest_overall_score ?? 0;
          bValue = b.summary.latest_overall_score ?? 0;
          break;
        case 'last_active':
          aValue = a.summary.last_active_at
            ? new Date(a.summary.last_active_at).getTime()
            : 0;
          bValue = b.summary.last_active_at
            ? new Date(b.summary.last_active_at).getTime()
            : 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [data?.summaries, searchTerm, sortField, sortDirection]);

  return {
    data,
    loading,
    error,
    days,
    setDays: handleSetDays,
    refetch,
  startDate,
  setStartDate: setStartDateAndResetSkip,
  endDate,
  setEndDate: setEndDateAndResetSkip,
  studentId,
  setStudentId: setStudentIdAndResetSkip,
  classId,
  setClassId: setClassIdAndResetSkip,
  grade,
  setGrade: setGradeAndResetSkip,
    skip,
    setSkip,
    limit,
    setLimit,
    dateRangeError,
    totalFromResponse,
    resetFilters,
    searchTerm,
    setSearchTerm,
    sortField,
    setSortField,
    sortDirection,
    setSortDirection,
    filteredAndSortedData,
  };
}
